import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Truck, Clock } from "lucide-react";

/**
 * Same-Day Print & Ship badge for the homepage hero.
 *
 * Cut-off: 11:00 AM CT (America/Chicago). Counts down to the upcoming
 * cut-off; after cut-off rolls past it transparently flips the message to
 * "Cut-off passed — next batch tomorrow 11 AM CT" and resumes counting to
 * tomorrow's 11 AM.
 */
const CT_TZ = "America/Chicago";

function nextCutoff() {
  // Get current "wall-clock" time in Chicago via Intl
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CT_TZ,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const ctHour   = Number(parts.hour);
  const ctMinute = Number(parts.minute);
  const ctSecond = Number(parts.second);

  // CT-local seconds since midnight RIGHT NOW
  const ctSecOfDay = ctHour * 3600 + ctMinute * 60 + ctSecond;
  const cutoffSecOfDay = 11 * 3600; // 11:00 AM CT
  const remainingToday = cutoffSecOfDay - ctSecOfDay;

  if (remainingToday > 0) {
    return { secondsLeft: remainingToday, passed: false };
  }
  // After today's cutoff → count to tomorrow's 11 AM CT
  const secsUntilCtMidnight = 24 * 3600 - ctSecOfDay;
  return { secondsLeft: secsUntilCtMidnight + cutoffSecOfDay, passed: true };
}

function pad(n) { return String(n).padStart(2, "0"); }

export default function SameDayBadge({ className = "", testid = "same-day-badge" }) {
  const [{ secondsLeft, passed }, setT] = useState(nextCutoff);

  useEffect(() => {
    const id = setInterval(() => setT(nextCutoff()), 1000);
    return () => clearInterval(id);
  }, []);

  const hh = Math.floor(secondsLeft / 3600);
  const mm = Math.floor((secondsLeft % 3600) / 60);
  const ss = secondsLeft % 60;

  return (
    <Link
      to="/same-day"
      data-testid={testid}
      aria-label="Same Day Print & Ship — view lineup"
      className={
        "group inline-flex items-stretch rounded-sm overflow-hidden shadow-[0_8px_28px_rgba(254,77,15,0.50)] " +
        "ring-1 ring-black/20 transition-transform hover:scale-[1.015] hover:shadow-[0_10px_34px_rgba(254,77,15,0.62)] " +
        className
      }
    >
      {/* ── Left rail with icon ── */}
      <div className="flex flex-col items-center justify-center bg-[#C4380C] text-white px-3 pt-0 pb-3 shrink-0">
        <Zap className="w-4 h-4 mb-0" fill="currentColor" />
        <span className="font-mono text-[7px] uppercase tracking-[0.18em] leading-none whitespace-nowrap mt-0.5">SAME&nbsp;DAY</span>
      </div>

      {/* ── Main copy block ── */}
      <div className="bg-gradient-to-b from-[#FE4D0F] to-[#E03E08] text-white px-4 pt-0 pb-2.5 min-w-0">
        <div className="font-display font-black text-[15px] sm:text-base leading-tight tracking-tight">
          Same Day Print &amp; Ship Kolt Products
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Clock className="w-3 h-3 shrink-0" />
          {passed ? (
            <span className="font-mono text-[10px] uppercase tracking-wider leading-none">
              Cut-off passed · next batch tomorrow 11 AM CT
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-wider leading-none">
              Cut-off 11 AM CT · ships today
            </span>
          )}
        </div>

        {/* ── Countdown ── */}
        <div className="flex items-end gap-1.5 mt-2 font-mono tabular-nums leading-none">
          <CountChunk value={hh} label="hrs" />
          <span className="text-white/80 text-base pb-0.5">:</span>
          <CountChunk value={mm} label="min" />
          <span className="text-white/80 text-base pb-0.5">:</span>
          <CountChunk value={ss} label="sec" pulse />
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/80 pb-0.5 ml-1.5">
            {passed ? "until tomorrow" : "left"}
          </span>
        </div>
      </div>

      {/* ── Right rail with truck CTA ── */}
      <div className="hidden md:flex flex-col items-center justify-center bg-[#C4380C] text-white px-3 pt-0 pb-3 shrink-0 border-l border-white/15">
        <Truck className="w-4 h-4 mb-0" />
        <span className="font-mono text-[7px] uppercase tracking-[0.18em] leading-none mt-0.5">View&nbsp;lineup&nbsp;→</span>
      </div>
    </Link>
  );
}

function CountChunk({ value, label, pulse = false }) {
  return (
    <span className="flex flex-col items-center">
      <span className={`text-lg font-black ${pulse ? "animate-pulse" : ""}`}>{pad(value)}</span>
      <span className="text-[7px] uppercase tracking-widest text-white/80 leading-none mt-0.5">{label}</span>
    </span>
  );
}
